import { PreloadableComponent, lazyWithPreload } from "@/helpers/lazyWithPreload"
import CheckOrgExistence from "@/pages/CheckOrgExistence"
import ErrorPage from "@/pages/error-page/ErrorPage"
import { NotFoundProps } from "@/pages/not-found/NotFound"
import { ROUTES } from "@/router/routes"
import { Children, FC, ReactNode, isValidElement, lazy, memo } from "react"
import type { RouteObject } from "react-router-dom"
import { Navigate, RouterProvider, createBrowserRouter, matchRoutes, useLocation } from "react-router-dom"
import { AuthLayout, PlatformLayout, WebsiteLayout } from "src/components/layout"
import { TasksPageProps } from "./tasks/Tasks"

// The dashboard shell (sidebar, notifications, file cards with pdf.js) loads only when a dashboard route opens, so the
// public website never downloads it.
const DashboardLayout = lazy<FC>(() =>
  import("@/components/layout/DashboardLayout").then(module => ({ default: module.DashboardLayout }))
)
const NoSidebarLayout = lazy<FC>(() =>
  import("@/components/layout/NoSidebarLayout").then(module => ({ default: module.NoSidebarLayout }))
)
// The prerendered public pages are lazyWithPreload, so a page can be loaded before React hydrates it (preloadPage).
const Software = lazyWithPreload(() => import("@/pages/website-landing/software"))
const NotFound = lazy<React.FC<NotFoundProps>>(() => import("./not-found/NotFound"))
const Overview = lazy<FC>(() => import("./overview/Overview"))
const Project = lazy<FC>(() => import("@/pages/project/Project"))
const ProjectsPage = lazy<FC>(() => import("./projects-page/ProjectsPage"))
const Companies = lazy<FC>(() => import("./companies/Companies"))
const EmailLogs = lazy<FC>(() => import("./email-logs/EmailLogs"))
const Company = lazy<FC>(() => import("./company/Company"))
const ServiceApplications = lazy<FC>(() => import("./service-applications/ServiceApplications"))
const GovernmentWork = lazy<FC>(() => import("./government-work"))
const CreateAoi = lazy<FC>(() => import("./create-aoi/CreateAoi"))
const CreateApp = lazy<FC>(() => import("./create-app/CreateApp"))
const Application = lazy<FC>(() => import("./application/ApplicationNew"))
const Auth = lazy<FC>(() => import("@/pages/auth/Auth"))
const PrivacyPolicy = lazyWithPreload(() => import("./privacy-policy/PrivacyPolicyTerms"))
const PreparingGeometry = lazyWithPreload(() => import("./preparing-geometry/PreparingGeometry"))
const Home = lazyWithPreload(() => import("@/pages/website-landing/home/index"))
const AoiHome = lazyWithPreload(() => import("@/pages/website-landing/aoi-home/index"))
// const Industries = lazy<FC>(() => import("@/pages/website-landing/industries/index"))
const OurServices = lazyWithPreload(() => import("@/pages/website-landing/our-services/index"))
const Consulting = lazyWithPreload(() => import("@/pages/website-landing/consulting/index"))
const Blog = lazyWithPreload(() => import("@/pages/website-landing/blog/index"))
const Profile = lazy<FC>(() => import("./profile/Profile"))
const ProjectApplications = lazy<FC>(() => import("./applications/Applications"))
const TemplateSubjects = lazy<FC>(() => import("./template-subjects/TemplateSubjects"))
const TemplateSubject = lazy<FC>(() => import("./template-subject/TemplateSubject"))
const TemplateDocument = lazy<FC>(() => import("./template-document/TemplateDocument"))
const CreateNote = lazy<FC>(() => import("./create-note/CreateNote"))
const Tasks = lazy<FC<TasksPageProps>>(() => import("./tasks/Tasks"))
const CreateTask = lazy<FC>(() => import("./create-task/CreateTask"))
const Customers = lazy<FC>(() => import("./customers/Customers"))
const Employees = lazy<FC>(() => import("./employees/Employees"))
const Users = lazy<FC>(() => import("./users/Users"))
const TemplatesGroups = lazy<FC>(() => import("./template-groups/TemplateGroups"))
const ApplicationReports = lazy<FC>(() => import("./application-reports/ApplicationReports"))
const Reports = lazy<FC>(() => import("./reports/Reports"))
const PdfDocumentPreview = lazy<FC>(() => import("./pdf-documents-preview/AppLetter"))
const ManagerMap = lazy<FC>(() => import("./manager-map/ManagerMap"))
const Quote = lazyWithPreload(() => import("@/pages/website-landing/quote/index"))
const PlatformSettings = lazy<FC>(() => import("@/pages/platform-settings"))
const RedirectToHome = lazy<FC>(() => import("@/pages/RedirectToHome"))
const RedirectOldDashboard = lazy<FC>(() => import("@/pages/RedirectOldDashboard"))
const RootHome = lazyWithPreload(() => import("@/pages/RootHome"))
const QuoteRequests = lazy<FC>(() => import("@/pages/quote-requests/QuoteRequests"))
const CompanyUpdateRequests = lazy<FC>(() => import("@/pages/company-update-requests/CompanyUpdateRequests"))
const ChannelPartners = lazyWithPreload(() => import("@/pages/website-landing/channel-partners/index"))
const Enforcements = lazy<FC>(() => import("@/pages/enforcements/Enforcements"))
const AssistanceRequests = lazy<FC>(() => import("@/pages/assistance-requests/AssistanceRequests"))
const LicenseUpdateRequest = lazy<FC>(() => import("@/pages/license-update-request/LicenseUpdateRequests"))
const Emails = lazy<FC>(() => import("@/pages/emails/Emails"))
const AppDetails = lazy<FC>(() => import("@/pages/create-app/app-details/AppDetails"))
const AoiDetails = lazy<FC>(() => import("@/pages/create-aoi/aoi-details/AoiDetails"))
const AoiLocation = lazy<FC>(() => import("@/pages/create-aoi/aoi-location/AoiLocation"))
const AoiDocuments = lazy<FC>(() => import("@/pages/create-aoi/aoi-documents/AoiDocuments"))
const AppLocation = lazy<FC>(() => import("@/pages/create-app/app-location/AppLocation"))
const AppDocuments = lazy<FC>(() => import("@/pages/create-app/app-documents/AppDocuments"))
const AoiSummaryReport = lazy<FC>(() => import("@/pages/create-aoi/aoi-summary-report/AoiSummaryReport"))
const SummaryReport = lazy<FC>(() => import("@/pages/create-app/summary-report/SummaryReport"))
const MapGeometry = lazy<FC>(() => import("@/pages/map-geometry/MapGeometry"))
const OrgSettings = lazy<FC>(() => import("@/pages/org-settings/OrgSettings"))
const ThankYouPage = lazy<FC>(() => import("@/pages/website-landing/thank-you-page/ThankYouPage"))
const Aois = lazy<FC>(() => import("@/pages/aois/Aois"))
const Aoi = lazy<FC>(() => import("@/pages/aoi/aoi"))
const Surveys = lazy<FC>(() => import("@/pages/surveys/Surveys"))

const { dashboard, website } = ROUTES

/** The page's old address. Reads the path from the router, not `window`: this module is also imported at build time. */
const RedirectSingleSubmission: FC = () => {
  const { pathname } = useLocation()
  return <Navigate to={pathname.replace("single-submission", "one-submission")} replace />
}

export const routes: RouteObject[] = [
  {
    path: "/dashboard/*",
    element: <RedirectOldDashboard />,
  },
  {
    path: "",
    element: <WebsiteLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <RootHome />,
      },
      {
        path: `/:org/${website.home}`,
        element: (
          <CheckOrgExistence>
            <Home />
          </CheckOrgExistence>
        ),
      },
      {
        path: `/:org/${website.software}`,
        element: (
          <CheckOrgExistence>
            <Software />
          </CheckOrgExistence>
        ),
      },
      {
        path: `/:org/${website.aoi}`,
        element: (
          <CheckOrgExistence>
            <AoiHome />
          </CheckOrgExistence>
        ),
      },
      {
        path: `/:org`,
        element: <RedirectToHome />,
      },
      {
        path: `/:org/${website.ourServices}`,
        element: (
          <CheckOrgExistence>
            <OurServices />
          </CheckOrgExistence>
        ),
      },
      {
        path: `/:org/${website.consulting}`,
        element: (
          <CheckOrgExistence>
            <Consulting />
          </CheckOrgExistence>
        ),
      },
      {
        path: "/:org/single-submission",
        element: <RedirectSingleSubmission />,
      },
      {
        path: `/:org/${website.channelPartners}`,
        element: (
          <CheckOrgExistence>
            <ChannelPartners />
          </CheckOrgExistence>
        ),
      },
      {
        path: `/:org/${website.blog}`,
        element: (
          <CheckOrgExistence>
            <Blog />
          </CheckOrgExistence>
        ),
      },
      {
        path: `/:org/${website.quote}`,
        element: (
          <CheckOrgExistence>
            <Quote />
          </CheckOrgExistence>
        ),
      },
      {
        path: `/:org/${website.thankYouPage}`,
        element: (
          <CheckOrgExistence>
            <ThankYouPage />
          </CheckOrgExistence>
        ),
      },
      {
        path: website.privacyPolicy,
        element: <PrivacyPolicy />,
      },
      {
        path: website.termsAndConditions,
        element: <PrivacyPolicy />,
      },
      {
        path: website.preparingGeometry,
        element: <PreparingGeometry />,
      },
      {
        path: "/:org/*",
        element: (
          <CheckOrgExistence>
            <NotFound mode={"page"} />
          </CheckOrgExistence>
        ),
      },
      {
        path: "*",
        element: <NotFound mode={"org"} />,
      },
    ],
  },
  {
    element: <AuthLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <Auth />,
        path: `/:org/${website.auth}/:portal/:mode`,
      },
    ],
  },
  {
    path: `/:org/${dashboard.index}`,
    element: <PlatformLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <Navigate to={dashboard.overview} />,
          },
          {
            path: dashboard.overview,
            Component: Overview,
          },
          {
            path: `${dashboard.projects}/:key`,
            Component: ProjectsPage,
          },
          {
            path: `${dashboard.aois}`,
            Component: Aois,
          },
          {
            path: `${dashboard.aoi}/:key`,
            Component: Aoi,
          },
          {
            path: dashboard.surveys,
            Component: Surveys,
          },
          {
            path: dashboard.enforcements,
            Component: Enforcements,
          },
          {
            path: dashboard.assistanceRequests,
            Component: AssistanceRequests,
          },
          {
            path: dashboard.mapGeometry,
            Component: MapGeometry,
          },
          {
            path: dashboard.settings,
            children: [
              {
                index: true,
                element: <Navigate to={"general"} replace />,
              },
              {
                path: ":key",
                Component: OrgSettings,
              },
            ],
          },
          {
            path: dashboard.applications,
            Component: ProjectApplications,
          },
          {
            path: `${dashboard.companies}/:key`,
            Component: Companies,
          },
          {
            path: `${dashboard.emailLogs}/:key`,
            Component: EmailLogs,
          },
          {
            path: `${dashboard.company}/:menu`,
            Component: Company,
          },
          {
            path: dashboard.customers,
            element: <Customers />,
          },
          {
            path: dashboard.employees,
            element: <Employees />,
          },
          {
            path: dashboard.users,
            element: <Users />,
          },
          {
            path: dashboard.tasks,
            element: <Tasks mode={"default"} />,
          },
          {
            path: `${dashboard.platformSettings}/:key`,
            element: <PlatformSettings />,
          },
          {
            path: `${dashboard.project}/:category/:key`,
            element: <Project />,
          },
          {
            path: dashboard.projectTasks,
            element: <Tasks mode={"project"} />,
          },
          {
            path: dashboard.emails,
            element: <Emails />,
          },
          {
            path: dashboard.agentProfessionalsTasks,
            element: <Tasks mode={"readonly"} />,
          },
          {
            path: dashboard.task,
            element: <CreateTask />,
          },
          {
            path: dashboard.quoteRequests,
            element: <QuoteRequests />,
          },
          {
            path: dashboard.companyUpdateRequests,
            element: <CompanyUpdateRequests />,
          },
          {
            path: dashboard.licenseUpdateRequests,
            element: <LicenseUpdateRequest />,
          },
          {
            path: dashboard.templateSubjects,
            element: <TemplateSubjects />,
          },
          {
            path: dashboard.applicationReports,
            element: <ApplicationReports />,
          },
          {
            path: dashboard.reports,
            element: <Reports />,
          },
          {
            path: dashboard.projectApplications,
            element: <ProjectApplications />,
          },
          {
            path: dashboard.serviceProviderApplications,
            element: <ServiceApplications />,
          },
          {
            path: dashboard.governmentWorks,
            element: <GovernmentWork />,
          },
          {
            path: `${dashboard.application}/:url`,
            element: <Application />,
          },
          {
            path: `${dashboard.profile}/:step`,
            element: <Profile />,
          },
          {
            path: dashboard.templateGroups,
            element: <TemplatesGroups />,
          },
          {
            path: dashboard.templateSubject,
            element: <TemplateSubject />,
          },
          {
            path: dashboard.templateDocument,
            element: <TemplateDocument />,
          },
          {
            path: "pdf-document-preview",
            element: <PdfDocumentPreview />,
          },
          {
            path: dashboard.managerMap,
            element: <ManagerMap />,
          },
        ],
      },
      {
        element: <NoSidebarLayout />,
        children: [
          {
            path: dashboard.createTask,
            element: <CreateTask />,
          },
          {
            path: dashboard.createNote,
            element: <CreateNote />,
          },
          {
            path: dashboard.createProject,
            element: <CreateApp />,
            errorElement: <ErrorPage />,
            children: [
              {
                path: "",
                element: <Navigate to={`details`} />,
              },
              {
                path: "details",
                element: <AppDetails />,
              },
              {
                path: "location",
                element: <AppLocation />,
              },
              {
                path: "documents",
                element: <AppDocuments />,
              },
              {
                path: "summary-report",
                element: <SummaryReport />,
              },
            ],
          },
          {
            path: dashboard.createAoi,
            element: <CreateAoi />,
            errorElement: <ErrorPage />,
            children: [
              {
                path: "",
                element: <Navigate to={`details`} />,
              },
              {
                path: "details",
                element: <AoiDetails />,
              },
              {
                path: "location",
                element: <AoiLocation />,
              },
              {
                path: "documents",
                element: <AoiDocuments />,
              },
              {
                path: "summary-report",
                element: <AoiSummaryReport />,
              },
            ],
          },
          {
            path: "*",
            element: <NotFound mode={"page"} />,
          },
        ],
      },
    ],
  },
]

/** The preloads of the lazyWithPreload pages in a route's element. */
const pagePreloads = (node: ReactNode): PreloadableComponent<object>["preload"][] => {
  if (!isValidElement<{ children?: ReactNode }>(node)) return []
  const { preload } = node.type as Partial<PreloadableComponent<object>>
  return [...(preload ? [preload] : []), ...Children.toArray(node.props.children).flatMap(pagePreloads)]
}

/**
 * Loads the page the route for `pathname` renders, so src/index.tsx can hydrate a prerendered page in one pass. The
 * prerendered public pages are lazyWithPreload for this.
 */
export const preloadPage = (pathname: string): Promise<unknown> =>
  Promise.all(
    (matchRoutes(routes, pathname) ?? []).flatMap(({ route }) => pagePreloads(route.element)).map(preload => preload())
  )

const Router = () => <RouterProvider router={createBrowserRouter(routes)} />
export default memo(Router)
